import { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';

const HeroVideo = forwardRef(({ videoUrl, transform, onError: onErrorProp }, ref) => {
  const [isPortrait, setIsPortrait] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef(null);
  const bgVideoRef = useRef(null);
  const MAX_RETRIES = 2;

  useImperativeHandle(ref, () => ({
    unmute: () => {
      if (videoRef.current) {
        videoRef.current.muted = false;
        setIsMuted(false);
      }
    },
    mute: () => {
      if (videoRef.current) {
        videoRef.current.muted = true;
        setIsMuted(true);
      }
      if (bgVideoRef.current) {
        bgVideoRef.current.muted = true;
      }
    }
  }));

  useEffect(() => {
    setHasError(false);
    setIsReady(false);
    setRetryCount(0);
  }, [videoUrl]);

  const handleVideoLoaded = (e) => {
    const video = e.target;
    const isVideoPortrait = video.videoHeight > video.videoWidth;
    setIsPortrait(isVideoPortrait);
    setIsReady(true);
  };

  const handleVideoError = () => {
    if (retryCount < MAX_RETRIES) {
      setRetryCount(prev => prev + 1);
      setHasError(false);
      if (videoRef.current) {
        videoRef.current.load();
      }
    } else {
      setHasError(true);
      if (onErrorProp) onErrorProp();
    }
  };

  const handleCanPlay = () => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  };

  const videoSrc = retryCount > 0 ? `${videoUrl}${videoUrl.includes('?') ? '&' : '?'}retry=${retryCount}` : videoUrl;

  const transformStyle = transform ? {
    objectPosition: `${transform.positionX}% ${transform.positionY}%`,
    transform: `scale(${transform.zoom}) translateY(${-(transform.zoom - 1) * 18}%)`,
  } : {};

  if (hasError) return null;

  if (isPortrait && isReady) {
    return (
      <div 
        className="relative w-full h-[300px] sm:h-[400px] lg:h-[500px] rounded-2xl sm:rounded-3xl overflow-hidden"
        data-testid="hero-video-container"
      >
        <video
          ref={bgVideoRef}
          src={videoSrc}
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover scale-125 opacity-80"
          style={{ filter: 'blur(8px)' }}
        />
        <div className="absolute inset-0 bg-background/15" />
        <div className="absolute inset-0 flex items-center justify-center px-4">
          <video
            ref={videoRef}
            src={videoSrc}
            autoPlay
            loop
            muted={isMuted}
            playsInline
            controls
            className="h-full max-h-[90%] w-auto max-w-[90%] sm:max-w-[70%] lg:max-w-[60%] rounded-2xl shadow-2xl border border-border/30"
            style={transformStyle}
          />
        </div>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-background/80 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background/80 to-transparent" />
        </div>
      </div>
    );
  }

  return (
    <div 
      className="relative w-full h-[300px] sm:h-[400px] lg:h-[500px] rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl bg-card/30"
      data-testid="hero-video-container"
    >
      <video
        ref={videoRef}
        src={videoSrc}
        autoPlay
        loop
        muted={isMuted}
        playsInline
        controls
        preload="none"
        onLoadedMetadata={handleVideoLoaded}
        onCanPlay={handleCanPlay}
        onError={handleVideoError}
        className={`w-full h-full object-cover transition-opacity duration-500 ${isReady ? 'opacity-100' : 'opacity-0'}`}
        style={transformStyle}
      />
      {!isReady && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="animate-pulse-glow w-16 h-16 rounded-full bg-primary/20" />
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-background/60 to-transparent pointer-events-none" />
    </div>
  );
});

export default HeroVideo;
